import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import {
  CheckCircle2,
  ChefHat,
  Bike,
  Bell,
  XCircle,
  MapPin,
  Phone,
  Share2,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STAGES = [
  { key: "confirmed", label: "Order in", detail: "Payment received", Icon: CheckCircle2 },
  { key: "preparing", label: "In the kitchen", detail: "Mama is cooking", Icon: ChefHat },
  { key: "ready",     label: "Ready",       detail: "Time to eat", Icon: Bell },
  { key: "completed", label: "Complete",    detail: "Thanks for coming", Icon: Bike },
];

const POLL_INTERVAL_MS = 15000; // 15s — quick enough to feel live, kind to server.

/**
 * OrderTracker — public Uber-style tracking page shared via SMS.
 *
 * URL: /order/:sessionId — no login required. The backend endpoint returns
 * a scrubbed payload (first name only, no email/phone/prices) so the link
 * is safe to forward. Polls every 15s and pauses when the tab is hidden.
 */
export default function OrderTracker() {
  const { sessionId } = useParams();
  const [order, setOrder] = useState(null);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/orders/track/${sessionId}`);
      setOrder(data);
      setError(null);
    } catch (e) {
      const status = e?.response?.status;
      setError(status === 404 ? "not-found" : "unavailable");
    }
  }, [sessionId]);

  useEffect(() => {
    if (!sessionId) return;
    load();
    // Pause polling when the tab is hidden so we don't hit the API needlessly.
    const tick = setInterval(() => {
      if (document.visibilityState === "visible") load();
    }, POLL_INTERVAL_MS);
    return () => clearInterval(tick);
  }, [sessionId, load]);

  const shareUrl = typeof window !== "undefined" ? window.location.href : "";
  const share = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: "Mamamina's order", url: shareUrl }); return; }
      catch { /* user cancelled — ignore */ }
    }
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* clipboard denied */ }
  };

  if (error === "not-found") {
    return (
      <EmptyState
        title="We couldn't find that order"
        body="The link may have been mistyped. Double-check with the kitchen or place a new order below."
      />
    );
  }
  if (!order && !error) {
    return <SkeletonLoader />;
  }
  if (error) {
    return (
      <EmptyState
        title="Something went wrong"
        body="Try refreshing in a moment. If it keeps happening, ring mama on 07 3568 4708."
      />
    );
  }

  const cancelled = order.stage_index < 0;
  const currentStage = Math.max(0, order.stage_index);

  return (
    <main className="min-h-screen bg-[var(--mm-bg)] text-[var(--mm-cream)]" data-testid="order-tracker">
      <div className="max-w-2xl mx-auto px-6 py-16 md:py-24">
        {/* Header */}
        <div className="text-center mb-14">
          <p className="section-label mb-4">Live order status</p>
          <h1 className="font-display italic text-5xl md:text-6xl text-[var(--mm-cream)] leading-tight">
            {cancelled ? "Order cancelled" : `Thank you, ${order.first_name}.`}
          </h1>
          <p className="mt-4 text-[var(--mm-cream)]/60 max-w-md mx-auto">
            {cancelled
              ? "Your order was cancelled. Any charge has been refunded — please give it a few business days."
              : STAGES[currentStage]?.detail}
          </p>
        </div>

        {!cancelled ? (
          <>
            {/* Stage timeline */}
            <ol className="relative pl-10" data-testid="tracker-stages">
              {/* vertical rail */}
              <div className="absolute left-4 top-2 bottom-2 w-[2px] bg-[var(--mm-cream)]/10" />
              {STAGES.map((stage, i) => {
                const done = i < currentStage;
                const active = i === currentStage;
                const StageIcon = stage.Icon;
                return (
                  <li
                    key={stage.key}
                    className="relative pb-10 last:pb-0"
                    data-testid={`stage-${stage.key}${active ? "-active" : ""}`}
                  >
                    <div
                      className={`absolute -left-10 top-0 w-9 h-9 rounded-full border flex items-center justify-center transition-all duration-500 ${
                        done
                          ? "bg-[var(--mm-accent)] border-[var(--mm-accent)] text-[var(--mm-bg)]"
                          : active
                            ? "border-[var(--mm-accent)] text-[var(--mm-accent)] bg-[var(--mm-bg)] shadow-[0_0_0_6px_rgba(201,150,61,0.14)]"
                            : "border-[var(--mm-cream)]/15 text-[var(--mm-cream)]/30 bg-[var(--mm-bg)]"
                      }`}
                    >
                      <StageIcon size={16} strokeWidth={1.8} />
                    </div>
                    <p
                      className={`text-xs tracking-[0.3em] uppercase mb-1 ${
                        done || active ? "text-[var(--mm-accent)]" : "text-[var(--mm-cream)]/40"
                      }`}
                    >
                      Step {i + 1}
                    </p>
                    <p className={`font-display italic text-2xl leading-tight ${done || active ? "" : "text-[var(--mm-cream)]/50"}`}>
                      {stage.label}
                    </p>
                    <p className="text-sm text-[var(--mm-cream)]/55 mt-1">{stage.detail}</p>
                    {active && order.eta_minutes > 0 && i === 1 && (
                      <p className="text-xs text-[var(--mm-accent)] mt-2 tracking-wide">
                        ETA ~ {order.eta_minutes} min
                      </p>
                    )}
                  </li>
                );
              })}
            </ol>

            {/* Summary card */}
            <div className="mt-14 border border-[var(--mm-cream)]/15 bg-[var(--mm-charcoal)]/60 p-6">
              <div className="grid grid-cols-2 gap-6">
                <SummaryCell label="Items" value={order.item_count} />
                <SummaryCell
                  label="Total"
                  value={order.amount != null ? `$${(order.amount / 100).toFixed(2)} ${(order.currency || "AUD").toUpperCase()}` : "—"}
                />
                <SummaryCell label="Order ref" value={order.session_id.slice(-8).toUpperCase()} />
                <SummaryCell
                  label="Type"
                  value={(order.order_type || "takeaway").replace("_", " ")}
                />
              </div>
            </div>
          </>
        ) : (
          <XCircle size={48} className="mx-auto text-[var(--mm-accent)]/60" strokeWidth={1} />
        )}

        {/* Actions */}
        <div className="mt-12 flex flex-wrap gap-3 justify-center">
          <button
            type="button"
            onClick={share}
            className="btn-outline inline-flex items-center gap-2"
            data-testid="share-link-btn"
          >
            <Share2 size={14} /> {copied ? "Link copied" : "Share link"}
          </button>
          <a
            href="tel:+61735684708"
            className="btn-outline inline-flex items-center gap-2"
            data-testid="call-kitchen-btn"
          >
            <Phone size={14} /> Call the kitchen
          </a>
          <a
            href="https://www.google.com/maps/dir/?api=1&destination=1422+Beaudesert+Rd+Acacia+Ridge+QLD"
            target="_blank"
            rel="noreferrer"
            className="btn-outline inline-flex items-center gap-2"
          >
            <MapPin size={14} /> Directions
          </a>
        </div>

        <div className="mt-14 text-center">
          <Link
            to="/"
            className="text-xs tracking-[0.24em] uppercase text-[var(--mm-cream)]/50 hover:text-[var(--mm-accent)] transition-colors"
          >
            Back to Mamamina
          </Link>
        </div>
      </div>
    </main>
  );
}

function SummaryCell({ label, value }) {
  return (
    <div>
      <p className="text-[0.62rem] tracking-[0.3em] uppercase text-[var(--mm-accent)] mb-1.5">
        {label}
      </p>
      <p className="font-display italic text-xl text-[var(--mm-cream)] capitalize">{value}</p>
    </div>
  );
}

function SkeletonLoader() {
  return (
    <main className="min-h-screen bg-[var(--mm-bg)]" data-testid="tracker-skeleton">
      <div className="max-w-2xl mx-auto px-6 py-24 animate-pulse">
        <div className="h-4 w-32 bg-[var(--mm-cream)]/10 mx-auto mb-4" />
        <div className="h-12 w-3/4 bg-[var(--mm-cream)]/10 mx-auto mb-8" />
        <div className="h-4 w-2/3 bg-[var(--mm-cream)]/10 mx-auto" />
        <div className="mt-16 space-y-8">
          {[0,1,2,3].map((i) => (
            <div key={i} className="flex gap-4 items-center">
              <div className="w-9 h-9 rounded-full bg-[var(--mm-cream)]/10" />
              <div className="flex-1 h-6 bg-[var(--mm-cream)]/10" />
            </div>
          ))}
        </div>
      </div>
    </main>
  );
}

function EmptyState({ title, body }) {
  return (
    <main className="min-h-screen bg-[var(--mm-bg)] flex items-center justify-center px-6 text-center">
      <div className="max-w-md">
        <p className="section-label mb-4">Mamamina&apos;s Soulfeed</p>
        <h1 className="font-display italic text-4xl md:text-5xl text-[var(--mm-cream)] leading-tight">{title}</h1>
        <p className="mt-4 text-[var(--mm-cream)]/60">{body}</p>
        <Link to="/" className="btn-primary mt-8 inline-block">Back home</Link>
      </div>
    </main>
  );
}
